<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { messageReportsAPI } from 'dashboard/api/messageReports';
import MessageTrendChart from './components/message-data/MessageTrendChart.vue';
import StatCard from './components/message-data/StatCard.vue';
import StatMini from './components/message-data/StatMini.vue';
import DeliveryBar from './components/message-data/DeliveryBar.vue';

// ── Date range ──────────────────────────────────────────────────────────────
const today = new Date();
const defaultFrom = new Date(today);
defaultFrom.setDate(today.getDate() - 29);

const fromDate = ref(defaultFrom.toISOString().slice(0, 10));
const toDate = ref(today.toISOString().slice(0, 10));

// ── State ───────────────────────────────────────────────────────────────────
const loading = ref(false);
const error = ref(null);
const data = ref(null);

async function fetchSummary() {
  loading.value = true;
  error.value = null;
  try {
    const res = await messageReportsAPI.getSummary({
      from: fromDate.value,
      to: toDate.value,
    });
    data.value = res.data;
  } catch (e) {
    error.value =
      e?.response?.data?.error || e.message || 'Erro ao carregar dados';
  } finally {
    loading.value = false;
  }
}

onMounted(fetchSummary);
watch([fromDate, toDate], fetchSummary);

// ── Formatting helpers ──────────────────────────────────────────────────────
const fmt = n => new Intl.NumberFormat('pt-BR').format(n ?? 0);
const pct = n => `${(n ?? 0).toFixed(1)}%`;

// ── Derived data ────────────────────────────────────────────────────────────
const overview = computed(() => data.value?.overview ?? {});
const timeseries = computed(() => data.value?.timeseries ?? []);
const channels = computed(() => data.value?.by_channel_type ?? []);
const waOfficial = computed(() => data.value?.whatsapp?.official ?? {});
const waAlternative = computed(() => data.value?.whatsapp?.alternative ?? {});
const campaignItems = computed(() => data.value?.campaigns?.items ?? []);
const campaignSummary = computed(() => data.value?.campaigns?.summary ?? {});

const kpis = computed(() => [
  {
    label: 'Enviadas',
    value: fmt(overview.value.sent),
    sub: `${fmt(overview.value.attempted)} tentativas no total`,
    icon: 'i-lucide-send',
    tone: 'blue',
  },
  {
    label: 'Entregues',
    value: fmt(overview.value.delivered),
    sub: `${pct(overview.value.delivery_rate)} de taxa de entrega`,
    icon: 'i-lucide-check-check',
    tone: 'emerald',
  },
  {
    label: 'Lidas',
    value: fmt(overview.value.read),
    sub: `${pct(overview.value.read_rate)} das enviadas`,
    icon: 'i-lucide-eye',
    tone: 'violet',
  },
  {
    label: 'Falhas',
    value: fmt(overview.value.failed),
    sub: 'não foram enviadas',
    icon: 'i-lucide-triangle-alert',
    tone: 'red',
  },
]);

const hasEstimated = computed(
  () => (overview.value.estimated_delivered ?? 0) > 0
);

const STATUS_MAP = {
  draft: { label: 'Rascunho', class: 'bg-n-alpha-2 text-n-slate-11' },
  scheduled: {
    label: 'Agendada',
    class: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300',
  },
  running: {
    label: 'Em andamento',
    class:
      'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300',
  },
  paused: {
    label: 'Pausada',
    class:
      'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300',
  },
  finished: {
    label: 'Concluída',
    class:
      'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300',
  },
  cancelled: { label: 'Cancelada', class: 'bg-n-alpha-2 text-n-slate-11' },
  failed: {
    label: 'Falhou',
    class: 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300',
  },
};

const statusBadge = status => STATUS_MAP[status] || STATUS_MAP.draft;
</script>

<template>
  <div class="flex flex-col gap-6 p-6">
    <!-- Header -->
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h1 class="text-xl font-bold text-n-slate-12">Dados de Mensagens</h1>
        <p class="text-sm text-n-slate-10 mt-0.5">
          Mensagens enviadas x entregues por tipo de canal, WhatsApp Oficial vs
          Alternativa e campanhas
        </p>
      </div>

      <div
        class="flex items-center gap-2 bg-n-alpha-2 border border-n-weak rounded-lg px-3 py-2"
      >
        <span class="i-lucide-calendar w-4 h-4 text-n-slate-10" />
        <input
          v-model="fromDate"
          type="date"
          class="text-sm bg-transparent text-n-slate-12 outline-none"
        />
        <span class="text-n-slate-9">–</span>
        <input
          v-model="toDate"
          type="date"
          class="text-sm bg-transparent text-n-slate-12 outline-none"
        />
      </div>
    </div>

    <!-- Loading / Error -->
    <div
      v-if="loading"
      class="flex items-center justify-center py-20 text-n-slate-9"
    >
      <span class="i-lucide-loader-2 w-6 h-6 animate-spin mr-2" />
      Carregando métricas...
    </div>

    <div
      v-else-if="error"
      class="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-700/30 text-red-700 dark:text-red-400"
    >
      <span class="i-lucide-alert-circle w-5 h-5 shrink-0" />
      <span class="text-sm">{{ error }}</span>
      <button class="ml-auto text-xs underline" @click="fetchSummary">
        Tentar novamente
      </button>
    </div>

    <template v-else-if="data">
      <!-- Overview KPIs -->
      <div class="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          v-for="kpi in kpis"
          :key="kpi.label"
          :label="kpi.label"
          :value="kpi.value"
          :sub="kpi.sub"
          :icon="kpi.icon"
          :tone="kpi.tone"
        />
      </div>

      <!-- Delivery honesty note -->
      <div
        v-if="hasEstimated"
        class="flex gap-3 p-3 bg-amber-50 dark:bg-amber-900/10 rounded-lg border border-amber-200 dark:border-amber-700/30 text-amber-800 dark:text-amber-300"
      >
        <span class="i-lucide-info w-4 h-4 shrink-0 mt-0.5" />
        <p class="text-xs leading-relaxed">
          <strong>{{ fmt(overview.confirmed_delivered) }}</strong> entregas
          confirmadas pelo provedor ·
          <strong>{{ fmt(overview.estimated_delivered) }}</strong> estimadas.
          Canais sem confirmação de entrega (ex.: WhatsApp API Alternativa)
          consideram <em>enviada = entregue</em>; apenas falhas contam como não
          enviadas.
        </p>
      </div>

      <!-- Trend chart -->
      <div
        class="flex flex-col gap-4 p-5 bg-white dark:bg-n-alpha-2 rounded-xl border border-n-weak shadow-sm"
      >
        <div class="flex items-center justify-between">
          <h2
            class="text-sm font-semibold text-n-slate-12 flex items-center gap-2"
          >
            <span class="i-lucide-trending-up w-4 h-4 text-n-brand" />
            Enviadas × Entregues por dia
          </h2>
          <div class="flex items-center gap-4 text-xs text-n-slate-10">
            <span class="flex items-center gap-1.5">
              <span class="w-3 h-1.5 rounded-full bg-n-brand" /> Enviadas
            </span>
            <span class="flex items-center gap-1.5">
              <span class="w-3 h-1.5 rounded-full bg-emerald-500" /> Entregues
            </span>
          </div>
        </div>
        <MessageTrendChart :series="timeseries" />
      </div>

      <!-- Por tipo de canal -->
      <div
        class="flex flex-col gap-4 p-5 bg-white dark:bg-n-alpha-2 rounded-xl border border-n-weak shadow-sm"
      >
        <h2
          class="text-sm font-semibold text-n-slate-12 flex items-center gap-2"
        >
          <span class="i-lucide-layers w-4 h-4 text-n-brand" />
          Por Tipo de Canal
        </h2>

        <div
          v-if="channels.length === 0"
          class="py-8 text-center text-sm text-n-slate-9"
        >
          Nenhuma mensagem enviada no período.
        </div>

        <div v-else class="flex flex-col divide-y divide-n-weak">
          <div
            v-for="row in channels"
            :key="row.key"
            class="grid grid-cols-12 items-center gap-3 py-3"
          >
            <div class="col-span-12 sm:col-span-3 flex items-center gap-2">
              <span class="text-sm font-medium text-n-slate-12">{{
                row.label
              }}</span>
              <span
                class="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                :class="
                  row.tracked
                    ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'
                    : 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300'
                "
              >
                {{ row.tracked ? 'Confirmada' : 'Estimada' }}
              </span>
            </div>
            <div class="col-span-6 sm:col-span-3">
              <StatMini label="Enviadas" :value="fmt(row.sent)" />
            </div>
            <div class="col-span-6 sm:col-span-2">
              <StatMini label="Entregues" :value="fmt(row.delivered)" />
            </div>
            <div class="col-span-12 sm:col-span-4">
              <DeliveryBar
                :sent="row.sent"
                :delivered="row.delivered"
                :tone="row.tracked ? 'confirmed' : 'estimated'"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- WhatsApp: Oficial vs Alternativa -->
      <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <!-- Oficial -->
        <div
          class="flex flex-col gap-4 p-5 bg-white dark:bg-n-alpha-2 rounded-xl border border-n-weak shadow-sm"
        >
          <div class="flex items-start justify-between">
            <div>
              <h2
                class="text-sm font-semibold text-n-slate-12 flex items-center gap-2"
              >
                <span class="i-lucide-badge-check w-4 h-4 text-emerald-600" />
                WhatsApp · API Oficial
              </h2>
              <p class="text-xs text-n-slate-9 mt-0.5">WhatsApp Cloud (Meta)</p>
            </div>
            <span
              class="text-[10px] px-2 py-0.5 rounded-full font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300"
            >
              Entrega confirmada
            </span>
          </div>

          <div class="grid grid-cols-4 gap-2">
            <StatMini label="Enviadas" :value="fmt(waOfficial.sent)" large />
            <StatMini
              label="Entregues"
              :value="fmt(waOfficial.delivered)"
              large
            />
            <StatMini label="Lidas" :value="fmt(waOfficial.read)" large />
            <StatMini label="Falhas" :value="fmt(waOfficial.failed)" large />
          </div>

          <DeliveryBar
            :sent="waOfficial.sent"
            :delivered="waOfficial.delivered"
            tone="confirmed"
          />

          <div
            v-if="waOfficial.inboxes?.length"
            class="flex flex-col gap-2 pt-1"
          >
            <div
              v-for="ib in waOfficial.inboxes"
              :key="ib.inbox_id"
              class="flex items-center justify-between text-xs"
            >
              <span class="text-n-slate-11 truncate max-w-[55%]">{{
                ib.name
              }}</span>
              <span class="text-n-slate-9">
                {{ fmt(ib.delivered) }} / {{ fmt(ib.sent) }} ·
                {{ pct(ib.delivery_rate) }}
              </span>
            </div>
          </div>
          <div v-else class="py-4 text-center text-xs text-n-slate-9">
            Nenhum inbox WhatsApp Oficial com envios no período.
          </div>
        </div>

        <!-- Alternativa -->
        <div
          class="flex flex-col gap-4 p-5 bg-white dark:bg-n-alpha-2 rounded-xl border border-n-weak shadow-sm"
        >
          <div class="flex items-start justify-between">
            <div>
              <h2
                class="text-sm font-semibold text-n-slate-12 flex items-center gap-2"
              >
                <span class="i-lucide-zap w-4 h-4 text-amber-500" />
                WhatsApp · API Alternativa
              </h2>
              <p class="text-xs text-n-slate-9 mt-0.5">Z-API / não oficial</p>
            </div>
            <span
              class="text-[10px] px-2 py-0.5 rounded-full font-medium bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300"
            >
              Entrega estimada
            </span>
          </div>

          <div class="grid grid-cols-3 gap-2">
            <StatMini label="Enviadas" :value="fmt(waAlternative.sent)" large />
            <StatMini
              label="Entregues"
              :value="fmt(waAlternative.delivered)"
              large
            />
            <StatMini label="Falhas" :value="fmt(waAlternative.failed)" large />
          </div>

          <DeliveryBar
            :sent="waAlternative.sent"
            :delivered="waAlternative.delivered"
            tone="estimated"
          />

          <p class="text-[11px] leading-relaxed text-n-slate-9">
            A API Alternativa (Z-API) não retorna confirmação de entrega:
            consideramos <em>enviada = entregue</em> e uma falha significa que a
            mensagem <em>não foi enviada</em>.
          </p>

          <div v-if="waAlternative.inboxes?.length" class="flex flex-col gap-2">
            <div
              v-for="ib in waAlternative.inboxes"
              :key="ib.inbox_id"
              class="flex items-center justify-between text-xs"
            >
              <span class="text-n-slate-11 truncate max-w-[55%]">{{
                ib.name
              }}</span>
              <span class="text-n-slate-9">
                {{ fmt(ib.delivered) }} / {{ fmt(ib.sent) }} enviadas
              </span>
            </div>
          </div>
          <div v-else class="py-4 text-center text-xs text-n-slate-9">
            Nenhum inbox WhatsApp Alternativa com envios no período.
          </div>
        </div>
      </div>

      <!-- Campanhas -->
      <div
        class="flex flex-col gap-4 p-5 bg-white dark:bg-n-alpha-2 rounded-xl border border-n-weak shadow-sm"
      >
        <div class="flex flex-wrap items-center justify-between gap-3">
          <h2
            class="text-sm font-semibold text-n-slate-12 flex items-center gap-2"
          >
            <span class="i-lucide-megaphone w-4 h-4 text-n-brand" />
            Campanhas de WhatsApp
          </h2>
          <div class="flex items-center gap-4 text-xs text-n-slate-10">
            <span class="flex items-baseline gap-1">
              <strong class="text-n-slate-12">
                {{ fmt(campaignSummary.count) }}
              </strong>
              campanhas
            </span>
            <span class="flex items-baseline gap-1">
              <strong class="text-n-slate-12">
                {{ fmt(campaignSummary.recipients) }}
              </strong>
              destinatários
            </span>
            <span class="flex items-baseline gap-1">
              <strong class="text-n-slate-12">
                {{ pct(campaignSummary.delivery_rate) }}
              </strong>
              entrega
            </span>
          </div>
        </div>

        <div
          v-if="campaignItems.length === 0"
          class="py-8 text-center text-sm text-n-slate-9"
        >
          Nenhuma campanha ativa no período selecionado.
        </div>

        <div v-else class="overflow-x-auto">
          <table class="w-full text-sm min-w-[720px]">
            <thead>
              <tr
                class="text-left text-[11px] uppercase tracking-wide text-n-slate-9 border-b border-n-weak"
              >
                <th class="py-2 pr-3 font-medium">Campanha</th>
                <th class="py-2 px-3 font-medium">Transporte</th>
                <th class="py-2 px-3 font-medium text-right">Destinatários</th>
                <th class="py-2 px-3 font-medium text-right">Enviadas</th>
                <th class="py-2 px-3 font-medium text-right">Entregues</th>
                <th class="py-2 px-3 font-medium w-40">Taxa</th>
                <th class="py-2 px-3 font-medium text-right">Falhas</th>
                <th class="py-2 pl-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr v-for="c in campaignItems" :key="c.id">
                <td class="py-3 pr-3">
                  <div class="flex flex-col max-w-[200px]">
                    <span class="font-medium text-n-slate-12 truncate">
                      {{ c.name }}
                    </span>
                    <span class="text-xs text-n-slate-9 truncate">
                      {{ c.inbox_name }}
                    </span>
                  </div>
                </td>
                <td class="py-3 px-3">
                  <span
                    class="text-[10px] px-2 py-0.5 rounded-full font-medium"
                    :class="
                      c.transport === 'oficial'
                        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'
                        : 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300'
                    "
                  >
                    {{ c.transport === 'oficial' ? 'Oficial' : 'Alternativa' }}
                  </span>
                </td>
                <td class="py-3 px-3 text-right text-n-slate-11">
                  {{ fmt(c.total) }}
                </td>
                <td class="py-3 px-3 text-right text-n-slate-11">
                  {{ fmt(c.sent) }}
                </td>
                <td class="py-3 px-3 text-right text-n-slate-11">
                  {{ fmt(c.delivered) }}
                </td>
                <td class="py-3 px-3">
                  <DeliveryBar
                    :sent="c.sent"
                    :delivered="c.delivered"
                    :tone="c.tracked ? 'confirmed' : 'estimated'"
                  />
                </td>
                <td class="py-3 px-3 text-right text-n-slate-11">
                  {{ fmt(c.failed) }}
                </td>
                <td class="py-3 pl-3">
                  <span
                    class="text-[10px] px-2 py-0.5 rounded-full font-medium"
                    :class="statusBadge(c.status).class"
                  >
                    {{ statusBadge(c.status).label }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </div>
</template>
